import { useState, useRef } from "react";
import JSZip from "jszip";
import { toast } from "sonner";
import api, { formatApiError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Upload, FileText, CheckCircle2, AlertCircle, RefreshCw, FileArchive, AlertTriangle, X, Trash2 } from "lucide-react";

/**
 * Bulk PDF certificate importer.
 * - Auto-splits ZIPs > 25 MB into ≤20 MB chunks (client-side, JSZip)
 * - Each chunk becomes an independent background job (polled every 3s)
 * - No learner auto-create: unmatched rows shown for manual review
 * - Hybrid regex + GPT-5.2 text/vision extraction
 */
const SPLIT_THRESHOLD_BYTES = 25 * 1024 * 1024;   // above this we split
const SPLIT_CHUNK_TARGET_BYTES = 20 * 1024 * 1024;  // target size per chunk — well under any ingress cap

async function splitZipIfLarge(file, onProgress) {
  if (file.size <= SPLIT_THRESHOLD_BYTES) return [file];
  onProgress?.(`Splitting ${file.name} (${Math.round(file.size / (1024 * 1024))} MB)…`);
  const src = await JSZip.loadAsync(file);
  const entries = Object.values(src.files).filter(f => !f.dir && f.name.toLowerCase().endsWith(".pdf"));
  if (entries.length === 0) return [file];   // let the backend handle "no PDFs"

  // Read each PDF's compressed size (approx) so we can group without inflating memory too far
  const sized = [];
  for (const e of entries) {
    const bytes = await e.async("uint8array");
    sized.push({ name: e.name.split("/").pop(), bytes });
  }
  // Greedy pack into chunks
  const chunks = [];
  let cur = { files: [], size: 0 };
  for (const s of sized) {
    if (cur.size > 0 && cur.size + s.bytes.length > SPLIT_CHUNK_TARGET_BYTES) {
      chunks.push(cur);
      cur = { files: [], size: 0 };
    }
    cur.files.push(s);
    cur.size += s.bytes.length;
  }
  if (cur.files.length) chunks.push(cur);

  const results = [];
  for (let i = 0; i < chunks.length; i++) {
    onProgress?.(`Zipping chunk ${i + 1}/${chunks.length}…`);
    const out = new JSZip();
    for (const f of chunks[i].files) out.file(f.name, f.bytes);
    const blob = await out.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 3 } });
    const chunkFile = new File(
      [blob],
      `${file.name.replace(/\.zip$/i, "")}-part${i + 1}of${chunks.length}.zip`,
      { type: "application/zip", lastModified: Date.now() },
    );
    results.push(chunkFile);
  }
  return results;
}

export default function PdfZipImportSection() {
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [progress, setProgress] = useState("");
  const cancelledRef = useRef(false);

  const reset = () => { setFiles([]); setPreview(null); setProgress(""); };
  const removeFile = (key) => setFiles(prev => prev.filter(f => f.key !== key));

  const pollJob = async (jobId, zipName) => {
    // Poll every 3s until complete or failed. Tolerate transient errors
    // (backend overload during heavy extractions can 502/504 the ingress).
    let consecutiveErrors = 0;
    const MAX_CONSEC_ERRORS = 20;   // ~1 min of consecutive failures before giving up
    while (true) {
      if (cancelledRef.current) return null;
      await new Promise(r => setTimeout(r, 3000));
      try {
        const { data } = await api.get(`/import/jobs/${jobId}`, { timeout: 20000 });
        consecutiveErrors = 0;
        if (data.status === "complete" || data.status === "failed") return data;
        const pct = data.total ? Math.round((data.done / data.total) * 100) : 0;
        setProgress(`${zipName}: extracting ${data.done}/${data.total} (${pct}%)…`);
      } catch (err) {
        consecutiveErrors++;
        setProgress(`${zipName}: server busy, retrying… (${consecutiveErrors}/${MAX_CONSEC_ERRORS})`);
        if (consecutiveErrors >= MAX_CONSEC_ERRORS) {
          toast.error(`${zipName}: lost connection to the server. Job ID: ${jobId} — check the Bulk Import tab again in a minute; the job may still complete on the server.`, { duration: 20000 });
          return null;
        }
      }
    }
  };

  const runAll = async (dryRun) => {
    if (files.length === 0) { toast.error("Choose at least one ZIP file first"); return; }
    const setter = dryRun ? setLoading : setCommitting;
    setter(true);
    cancelledRef.current = false;
    setPreview(null);

    // Step 1: auto-split any oversized ZIPs client-side so no single POST hits proxy limits.
    const uploads = [];  // { file, sourceName }
    for (const { file } of files) {
      if (cancelledRef.current) break;
      let parts;
      try {
        parts = await splitZipIfLarge(file, (msg) => setProgress(msg));
      } catch (err) {
        toast.error(`${file.name}: could not read/split ZIP — ${err.message || err}`, { duration: 12000 });
        continue;
      }
      if (parts.length > 1) {
        toast.info(`${file.name} split into ${parts.length} chunks (each ≤ 20 MB) to avoid proxy limits.`);
      }
      parts.forEach(p => uploads.push({ file: p, sourceName: file.name }));
    }
    setProgress("");
    if (uploads.length === 0) { setter(false); return; }

    const agg = {
      dry_run: dryRun,
      summary: { total: 0, create: 0, skip: 0, unmatched: 0, error: 0, used_llm: 0 },
      rows: [],
    };
    let successZips = 0;
    let failedZips = 0;
    for (let i = 0; i < uploads.length; i++) {
      if (cancelledRef.current) break;
      const { file, sourceName } = uploads[i];
      setProgress(`Uploading ${i + 1}/${uploads.length}: ${file.name}  (${Math.round(file.size / (1024 * 1024))} MB)…`);
      const fd = new FormData();
      fd.append("file", file);
      let jobId = null;
      try {
        const { data } = await api.post(
          `/import/certificates-zip?dry_run=${dryRun}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" }, timeout: 15 * 60 * 1000 },
        );
        jobId = data.job_id;
      } catch (err) {
        const status = err.response?.status;
        const detail = formatApiError(err.response?.data?.detail);
        const msg = status
          ? `${file.name}: HTTP ${status} — ${detail}`
          : `${file.name}: upload failed (network) — ${err.message || "no server response"}`;
        toast.error(msg, { duration: 12000 });
        failedZips++;
        continue;
      }
      setProgress(`${file.name}: job ${jobId?.slice(0, 8)} queued — extracting…`);
      const finalDoc = await pollJob(jobId, file.name);
      if (!finalDoc) { failedZips++; continue; }
      if (finalDoc.status === "failed") {
        toast.error(`${file.name}: job failed — ${finalDoc.error || "unknown error"}. Job ID: ${jobId}`, { duration: 15000 });
        failedZips++;
        continue;
      }
      successZips++;
      for (const k of Object.keys(agg.summary)) {
        if (finalDoc.summary?.[k] != null) agg.summary[k] += finalDoc.summary[k];
      }
      (finalDoc.rows || []).forEach(r => agg.rows.push({ ...r, _zip: sourceName }));
    }
    setPreview(successZips ? agg : null);
    setProgress("");
    setter(false);
    if (cancelledRef.current) return;
    const s = agg.summary;
    if (successZips === 0) {
      toast.error(`All ${uploads.length} chunk${uploads.length === 1 ? "" : "s"} failed. Check the red toasts above and try again.`, { duration: 15000 });
    } else if (failedZips > 0) {
      toast.warning(`Partial: ${successZips}/${uploads.length} chunks processed. Result: ${s.create} to create · ${s.unmatched} unmatched · ${s.skip} skipped · ${s.error} errors.`, { duration: 12000 });
    } else {
      toast.success(
        dryRun
          ? `Preview: ${s.create} to create · ${s.unmatched} unmatched · ${s.skip} skipped · ${s.error} errors${s.used_llm ? ` · ${s.used_llm} LLM-assisted` : ""}`
          : `Imported ${s.create} certificates · ${s.unmatched} unmatched · ${s.skip} skipped · ${s.error} errors`,
      );
    }
  };

  const cancel = () => {
    cancelledRef.current = true;
    setLoading(false); setCommitting(false); setProgress("");
    toast.message("Cancelled — running job(s) will finish on the server.");
  };

  const summaryBadge = (label, count, tone) => (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded font-medium ${tone}`}>
      {label}: <strong>{count}</strong>
    </span>
  );

  return (
    <section className="bg-white rounded-lg border border-border" data-testid="pdf-zip-import-section">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FileArchive size={18} className="text-slate-600" />
          <h2 className="font-heading font-semibold">Bulk PDF Certificates (ZIP)</h2>
          <Badge variant="outline" className="text-[10px]">admin only</Badge>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 text-xs text-slate-700 space-y-1">
          <p><strong>How it works:</strong> upload runs as a background job. We extract text with pdfplumber; scanned image PDFs are rendered to PNG and sent to GPT-5.2 vision. The extractor pulls learner name, address/postcode, qual codes, cert number and dates.</p>
          <p className="text-slate-600"><strong>Strict matching:</strong> learners are matched by email → exact name → name + postcode. <em>No learners are ever auto-created.</em></p>
          <p className="text-slate-600"><strong>Duplicates skipped:</strong> same learner + qualification + issue date, OR same certificate number.</p>
          <p className="text-slate-500">Max 500 MiB per ZIP, 5,000 files, 15 MiB per PDF. Large ZIPs (&gt; 25 MB) are <strong>auto-split into ≤20 MB chunks</strong> client-side so no single upload trips a proxy limit. Each chunk becomes its own background job — safe to leave the tab open. Always run <strong>Preview</strong> first.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Label className="flex items-center gap-2 cursor-pointer">
            <input
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              multiple
              onChange={(e) => {
                const picked = Array.from(e.target.files || []).map(f => ({ file: f, key: `${f.name}-${f.size}-${f.lastModified}` }));
                setFiles(prev => {
                  const dedup = new Map(prev.map(x => [x.key, x]));
                  picked.forEach(p => dedup.set(p.key, p));
                  return Array.from(dedup.values());
                });
                setPreview(null);
                e.target.value = "";
              }}
              className="hidden"
              data-testid="zip-import-file"
            />
            <span className="inline-flex items-center gap-1.5 text-sm border border-border rounded px-3 py-1.5 hover:bg-slate-50">
              <Upload size={14} /> Choose ZIP(s)
            </span>
          </Label>

          <Button size="sm" variant="outline" disabled={files.length === 0 || loading || committing} onClick={() => runAll(true)} data-testid="zip-import-preview">
            {loading ? <RefreshCw size={14} className="mr-1.5 animate-spin" /> : null}
            Preview
          </Button>
          <Button
            size="sm"
            disabled={!preview || !preview.summary?.create || committing || loading}
            onClick={() => runAll(false)}
            data-testid="zip-import-commit"
          >
            {committing ? <RefreshCw size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
            Import {preview?.summary?.create ? `(${preview.summary.create})` : ""}
          </Button>
          {(loading || committing) && (
            <Button size="sm" variant="ghost" onClick={cancel} data-testid="zip-import-cancel">
              Stop polling
            </Button>
          )}
          {(files.length > 0 || preview) && !(loading || committing) && (
            <Button size="sm" variant="ghost" onClick={reset}>Reset</Button>
          )}
        </div>

        {files.length > 0 && (
          <div className="rounded border border-border bg-slate-50/40 divide-y divide-slate-200" data-testid="zip-import-file-list">
            {files.map(({ file, key }) => (
              <div key={key} className="px-3 py-2 flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText size={12} className="text-slate-400 shrink-0" />
                  <span className="truncate">{file.name}</span>
                  <span className="text-slate-400 shrink-0">({Math.round(file.size / (1024 * 1024))} MB)</span>
                </span>
                <button onClick={() => removeFile(key)} disabled={loading || committing} className="p-1 rounded hover:bg-red-50 text-red-500" title="Remove">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {progress && (
          <div className="text-xs text-slate-600 flex items-center gap-2" data-testid="zip-import-progress">
            <RefreshCw size={12} className="animate-spin" /> {progress}
          </div>
        )}

        {preview && (
          <div className="mt-2 space-y-3" data-testid="zip-import-result">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-slate-700">Result:</span>
              {summaryBadge("total", preview.summary.total, "bg-slate-100 text-slate-700")}
              {summaryBadge(preview.dry_run ? "to create" : "created", preview.summary.create, "bg-emerald-100 text-emerald-800")}
              {summaryBadge("unmatched", preview.summary.unmatched, "bg-orange-100 text-orange-800")}
              {summaryBadge("skipped", preview.summary.skip, "bg-amber-100 text-amber-800")}
              {summaryBadge("errors", preview.summary.error, "bg-red-100 text-red-800")}
              {preview.summary.used_llm > 0 && summaryBadge("LLM used", preview.summary.used_llm, "bg-violet-100 text-violet-800")}
              {preview.dry_run && preview.summary.create > 0 && (
                <span className="text-[11px] text-emerald-700 ml-1 inline-flex items-center gap-1">
                  <CheckCircle2 size={12} /> Dry-run preview — click Import to commit
                </span>
              )}
            </div>

            {preview.summary.unmatched > 0 && (
              <div className="rounded border border-orange-200 bg-orange-50 p-3 text-xs text-orange-800 flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <div>
                  <strong>{preview.summary.unmatched} certificate{preview.summary.unmatched === 1 ? "" : "s"} could not be matched to a learner in the system.</strong> These are listed below with their extracted data. Add the learner via Members → Add Student, then re-run the import to attach the PDF.
                </div>
              </div>
            )}

            <div className="border border-border rounded overflow-hidden max-h-[30rem] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-muted-foreground sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium">File</th>
                    <th className="text-left px-3 py-1.5 font-medium w-24">Status</th>
                    <th className="text-left px-3 py-1.5 font-medium">Learner</th>
                    <th className="text-left px-3 py-1.5 font-medium">Quals</th>
                    <th className="text-left px-3 py-1.5 font-medium">Issue</th>
                    <th className="text-left px-3 py-1.5 font-medium">Notes / Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, idx) => (
                    <tr key={idx} className="border-t border-border align-top" data-testid={`zip-import-row-${idx}`}>
                      <td className="px-3 py-1.5 font-mono text-[10px] text-slate-700 max-w-[220px] truncate" title={`${r._zip}: ${r.filename}`}>
                        {r.filename}
                        {r._zip && <span className="text-slate-400 block">↳ {r._zip}</span>}
                      </td>
                      <td className="px-3 py-1.5">
                        {r.status === "create" || r.status === "created" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12} /> {r.status}</span>
                        ) : r.status === "partial" ? (
                          <span className="text-emerald-700">partial</span>
                        ) : r.status === "unmatched" ? (
                          <span className="inline-flex items-center gap-1 text-orange-700"><AlertTriangle size={12} /> unmatched</span>
                        ) : r.status === "skip" ? (
                          <span className="text-amber-700">skip</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700"><X size={12} /> error</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-slate-700">
                        {r.learner || (r.extracted?.learner_name) || "—"}
                        {r.extracted?.postcode && <span className="text-slate-400 block">{r.extracted.postcode}</span>}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-[11px]">
                        {(r.qual_matched || r.extracted?.qual_codes || []).join(", ") || "—"}
                      </td>
                      <td className="px-3 py-1.5 tabular-nums">{r.extracted?.issue_date || "—"}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">
                        {(r.errors || []).map((e, i) => <div key={i} className="text-red-700"><AlertCircle size={10} className="inline mr-0.5" />{e}</div>)}
                        {(r.warnings || []).map((w, i) => <div key={i} className="text-amber-700">{w}</div>)}
                        {r.status === "unmatched" && r.extracted?.cert_numbers?.length > 0 && (
                          <div className="text-slate-500 mt-1">Cert #: {r.extracted.cert_numbers.join(", ")}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
